/**
 * The categories a borrower may upload are stored in DOCUMENTS_ALLOWED_TO_BE_UPLOADED as a
 * semicolon separated list of `schemaId:displayText` entries, so the services can name a
 * category without asking XDoc for its definitions again.
 *
 * A bare entry with no colon is one of the fixed upload names the portal offered before the
 * client could pick XDoc definitions ("Tax;Insurance"); those keep working as they are.
 */

import { DOCUMENTS_ALLOWED_TO_BE_UPLOADED_KEY } from "../constants/Configurator.constants";
import {
  DOCUMENT_TYPE_DISPLAY_SEPARATOR,
  DOCUMENT_UPLOAD_TYPES,
} from "../constants/StatementsDocuments.constants";

const getOptionKey = (option) =>
  typeof option === "string" ? option : option?.schemaId || option?.label || "";

const getOptionDisplayText = (option) =>
  typeof option === "string" ? option : (option?.displayText ?? option?.label ?? "");

const parseEntry = (token) => {
  const colon = token.indexOf(":");
  if (colon < 0) {
    return { key: token, displayText: token };
  }
  const key = token.substring(0, colon).trim();
  const displayText = token.substring(colon + 1).trim();
  return { key, displayText: displayText || key };
};

/** The configured categories as `{ key, displayText }`, in the order the admin saved them. */
export function parseDocumentUploadEntries(value) {
  if (!value?.length) {
    return [];
  }

  return value
    .split(";")
    .map((token) => token.trim())
    .filter(Boolean)
    .map(parseEntry)
    .filter((entry) => entry.key.length > 0);
}

export function parseDocumentUploadSelection(value) {
  return parseDocumentUploadEntries(value).map((entry) => entry.key);
}

export function serializeDocumentUploadSelection(selection = [], options = []) {
  return selection
    .map((key) => {
      const option = options.find((item) => getOptionKey(item) === key);
      const displayText = option ? getOptionDisplayText(option) : "";
      return displayText && displayText !== key ? `${key}:${displayText}` : key;
    })
    .join(";");
}

/**
 * The configured categories named for the borrower: the document on its own, since the category
 * it belongs to is of no interest while uploading. The category is kept where two documents
 * would otherwise read the same.
 */
export function nameDocumentUploadEntriesForBorrower(entries = []) {
  const documentNames = entries.map(
    (entry) => entry.displayText.split(DOCUMENT_TYPE_DISPLAY_SEPARATOR).pop().trim(),
  );

  return entries.map((entry, index) => {
    const documentName = documentNames[index];
    const isAmbiguous =
      documentNames.filter((name) => name === documentName).length > 1;

    return {
      ...entry,
      displayText:
        isAmbiguous || !documentName ? entry.displayText : documentName,
    };
  });
}

/**
 * The categories the portal offered before a client could select XDoc definitions. A client that
 * has never saved the setting keeps them, so the upload does not disappear for clients
 * configured before US-1027823.
 */
const CATEGORIES_BEFORE_THE_CLIENT_COULD_SELECT = DOCUMENT_UPLOAD_TYPES.map(
  (category) => ({ key: category.schemaId, displayText: category.label }),
);

/**
 * What the borrower may upload, named for them. The client's selection is the whole of it: a
 * client that saved a selection and then cleared it offers nothing, and the borrower is then
 * offered no upload at all, which is how the viewable-document pages already behave.
 */
export function getBorrowerUploadCategories(fields = []) {
  const configured = fields?.find(
    (item) => item.key === DOCUMENTS_ALLOWED_TO_BE_UPLOADED_KEY,
  );

  if (!configured) {
    return CATEGORIES_BEFORE_THE_CLIENT_COULD_SELECT;
  }

  return nameDocumentUploadEntriesForBorrower(
    parseDocumentUploadEntries(configured.value),
  );
}
